import {
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnInit,
  Output,
  SimpleChanges,
} from '@angular/core';
import { Transaction } from '../../models/transaction.model';
import { User } from '../../models/user.model';
import { Balance } from '../../models/balance.model';
import { Recipient } from '../../models/recipient.model';
import { CryptoRecipient } from '../../models/crypto-recipient';
import { RecipientService } from '../../services/recipient.service';
import { UserService } from '../../services/user.service';
import { ReturnTransactionPayload } from '../../services/debt.service';

@Component({
  selector: 'app-return-modal',
  standalone: false,
  templateUrl: './return-modal.component.html',
  styleUrl: './return-modal.component.css',
})
export class ReturnModalComponent implements OnInit, OnChanges {
  @Input() show = false;
  @Input() transaction!: Transaction;
  @Input() userUuid?: string;
  @Input() user?: User;

  @Output() close = new EventEmitter<void>();
  @Output() confirmRefund = new EventEmitter<ReturnTransactionPayload & { uuid: string }>();

  // Section 2: Recipient
  recipientMode: 'original_sender' | 'another_recipient' = 'original_sender';
  anotherRecipientType: 'sepa' | 'crypto' = 'sepa';
  bankRecipients: Recipient[] = [];
  cryptoRecipients: CryptoRecipient[] = [];
  selectedRecipientId: string = '';
  loadingRecipients = false;

  // Section 3: Amount, Currency, Balances & Exchange Rate
  availableCurrencies: Array<'EUR' | 'USD'> = ['USD', 'EUR'];
  selectedCurrency: 'EUR' | 'USD' = 'EUR';
  otherCurrency: 'EUR' | 'USD' = 'USD';
  returnAmount: number | null = null;
  note: string = 'Moneco - Return';

  isInvalidAmount = false;
  amountErrorMessage = '';

  // Balances
  userBalances: Balance[] = [];
  loadingUser = false;

  copiedField: string | null = null;

  constructor(
    private recipientService: RecipientService,
    private userService: UserService,
  ) {}

  ngOnInit(): void {
    this.initData();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['show'] && this.show) {
      this.initData();
    } else if (changes['transaction'] && this.transaction) {
      this.initData();
    }
  }

  initData(): void {
    if (!this.transaction) return;

    // Determine initial currency: locked to transaction initial currency
    const txCurrency = (this.transaction.amount_currency || '').toUpperCase();
    if (txCurrency.includes('USD')) {
      this.selectedCurrency = 'USD';
      this.otherCurrency = 'EUR';
    } else {
      this.selectedCurrency = 'EUR';
      this.otherCurrency = 'USD';
    }

    // Default amount: initial transaction amount
    const max = this.getMaxReturnAmount();
    this.returnAmount = max > 0 ? max : null;

    // Reset recipient selection
    this.recipientMode = 'original_sender';
    this.selectedRecipientId = '';

    // Load user & balances
    this.loadUserData();

    // Validate initial values
    this.validateAmount();
  }

  getUserUuid(): string {
    return this.userUuid || this.user?.uuid || this.transaction?.user?.uuid || '';
  }

  loadUserData(): void {
    if (this.user && this.user.balances && this.user.balances.length > 0) {
      this.userBalances = this.user.balances;
      return;
    }

    const uuid = this.getUserUuid();
    if (!uuid) return;

    this.loadingUser = true;
    this.userService.getUser(uuid).subscribe({
      next: (user) => {
        this.user = user;
        this.userBalances = user.balances || [];
        this.loadingUser = false;
      },
      error: () => {
        this.loadingUser = false;
      },
    });
  }

  // --- Recipient Management ---

  get senderDetails() {
    const b = this.transaction?.beneficiary;
    const s = this.transaction?.sender;
    const raw = this.transaction as any;
    const rawB = (b as any) || {};

    return {
      name:
        b?.name ||
        s?.name ||
        raw?.sender_name ||
        raw?.sender_full_name ||
        '',
      iban:
        b?.iban ||
        s?.iban ||
        raw?.sender_iban ||
        '',
      bic:
        b?.bic ||
        s?.bic ||
        raw?.sender_bic ||
        '',
      address: !this.isCryptoTransaction
        ? (b?.address || s?.address || raw?.address || rawB?.address || '')
        : '',
      crypto_address: this.isCryptoTransaction
        ? (rawB?.crypto_address ||
           rawB?.wallet_address ||
           s?.crypto_address ||
           raw?.sender_wallet_address ||
           raw?.from_address ||
           raw?.crypto_address ||
           b?.address ||
           '')
        : '',
      routing_number:
        rawB?.routing_number ||
        rawB?.sender_routing_number ||
        s?.routing_number ||
        raw?.sender_routing_number ||
        '',
      account_number:
        rawB?.account_number ||
        rawB?.sender_account_number ||
        s?.account_number ||
        raw?.sender_account_number ||
        '',
      bank_name:
        rawB?.bank_name ||
        rawB?.sender_bank_name ||
        s?.bank_name ||
        raw?.sender_bank_name ||
        '',
    };
  }

  get hasSenderDetails(): boolean {
    const d = this.senderDetails;
    return Boolean(
      d.name ||
        d.crypto_address ||
        d.iban ||
        d.bic ||
        d.routing_number ||
        d.account_number ||
        d.address,
    );
  }
  get initialTransactionType(): 'SEPA' | 'ACH' | 'CRYPTO' {
    const pm = (this.transaction?.payment_method || '').toUpperCase();
    const tt = (this.transaction?.transaction_type || '').toUpperCase();

    // 1. Bank transfer takes precedence
    if (
      tt === 'BANK_TRANSFER' ||
      ['SEPA', 'ACH', 'WIRE', 'BANK_TRANSFER'].includes(pm)
    ) {
      if (pm === 'ACH') {
        return 'ACH';
      }
      return 'SEPA';
    }

    // 2. Crypto deposit
    if (
      tt === 'CRYPTO_PAYMENT' ||
      tt === 'CRYPTO_DEPOSIT' ||
      pm === 'CRYPTO'
    ) {
      return 'CRYPTO';
    }

    return 'SEPA';
  }

  get isCryptoTransaction(): boolean {
    return this.initialTransactionType === 'CRYPTO';
  }

  get isAchTransaction(): boolean {
    return this.initialTransactionType === 'ACH';
  }

  get isSepaTransaction(): boolean {
    return this.initialTransactionType === 'SEPA';
  }

  get recipientTypeLabel(): string {
    if (this.isCryptoTransaction) return 'Crypto Recipient';
    if (this.isAchTransaction) return 'ACH Recipient';
    return 'SEPA Recipient';
  }

  toggleSelectAnotherRecipient(): void {
    this.recipientMode = 'another_recipient';
    this.loadRecipients();
  }

  useOriginalSender(): void {
    this.recipientMode = 'original_sender';
    this.selectedRecipientId = '';
  }

  loadRecipients(): void {
    const uuid = this.getUserUuid();
    if (!uuid) return;

    this.loadingRecipients = true;

    if (this.isCryptoTransaction) {
      if (this.cryptoRecipients.length > 0) {
        this.loadingRecipients = false;
        return;
      }
      this.recipientService.getCryptoRecipients(uuid, 1).subscribe({
        next: (res) => {
          this.cryptoRecipients = res.results || [];
          this.loadingRecipients = false;
        },
        error: () => {
          this.loadingRecipients = false;
        },
      });
    } else {
      if (this.bankRecipients.length > 0) {
        this.loadingRecipients = false;
        return;
      }
      this.recipientService.getRecipients(uuid, 1).subscribe({
        next: (res) => {
          this.bankRecipients = res.results || [];
          this.loadingRecipients = false;
        },
        error: () => {
          this.loadingRecipients = false;
        },
      });
    }
  }

  getSelectedRecipientObj(): any {
    if (this.recipientMode === 'original_sender') {
      return this.senderDetails;
    }

    if (this.isCryptoTransaction) {
      return this.cryptoRecipients.find(
        (r) => r.uuid === this.selectedRecipientId,
      );
    } else {
      return this.bankRecipients.find(
        (r) => r.uuid === this.selectedRecipientId,
      );
    }
  }

  // --- Amount & Balances ---

  getMaxReturnAmount(): number {
    if (!this.transaction) return 0;
    if (this.selectedCurrency === 'EUR') {
      const euroAmount = parseFloat(this.transaction.equivalent_in_euros);
      if (!isNaN(euroAmount) && euroAmount > 0) return euroAmount;
    }
    const standardAmount = parseFloat(this.transaction.amount);
    return isNaN(standardAmount) ? 0 : standardAmount;
  }

  setMaxAmount(): void {
    this.returnAmount = this.getMaxReturnAmount();
    this.validateAmount();
  }

  getBalanceForCurrency(currency: 'EUR' | 'USD'): number {
    if (!this.userBalances || !this.userBalances.length) return 0;
    const norm = currency.toUpperCase();
    const match = this.userBalances.find((b) => {
      const c = (b.currency || '').toUpperCase();
      if (norm === 'EUR') return c === 'EUR' || c === 'EURC';
      if (norm === 'USD') return c === 'USD' || c === 'USDC';
      return c === norm;
    });

    if (!match || !match.balance) return 0;
    const parsed = parseFloat(match.balance);
    return isNaN(parsed) ? 0 : parsed;
  }

  get currentUserSelectedBalance(): number {
    return this.getBalanceForCurrency(this.selectedCurrency);
  }

  get currentUserOtherBalance(): number {
    return this.getBalanceForCurrency(this.otherCurrency);
  }

  validateAmount(): void {
    const max = this.getMaxReturnAmount();
    if (
      this.returnAmount == null ||
      isNaN(this.returnAmount) ||
      this.returnAmount <= 0
    ) {
      this.isInvalidAmount = true;
      this.amountErrorMessage = 'Please enter a valid amount greater than 0.';
      return;
    }

    if (this.returnAmount > max) {
      this.isInvalidAmount = true;
      this.amountErrorMessage = `Amount must be lower than or equal to initial transaction amount (${max.toFixed(2)} ${this.selectedCurrency}).`;
      return;
    }

    this.isInvalidAmount = false;
    this.amountErrorMessage = '';
  }



  copyToClipboard(text: string, fieldName: string): void {
    if (!text) return;
    navigator.clipboard.writeText(text);
    this.copiedField = fieldName;
    setTimeout(() => {
      if (this.copiedField === fieldName) {
        this.copiedField = null;
      }
    }, 2000);
  }

  // --- Submit ---

  submit(): void {
    this.validateAmount();

    if (this.isInvalidAmount) {
      return;
    }

    if (
      this.recipientMode === 'another_recipient' &&
      !this.selectedRecipientId
    ) {
      alert(
        'Please select an existing recipient for the return, or revert to the original sender.',
      );
      return;
    }

    const payload: ReturnTransactionPayload & { uuid: string } = {
      uuid: this.transaction.uuid,
      amount: this.returnAmount!,
      note: this.note.trim(),
    };

    if (
      this.recipientMode === 'another_recipient' &&
      this.selectedRecipientId
    ) {
      payload.recipient_uuid = this.selectedRecipientId;
    }

    this.confirmRefund.emit(payload);
    this.close.emit();
  }
}
